/**
 * Enterprise-Grade Business, Management & Auditing Knowledge Base
 * 
 * Comprehensive coverage for:
 * - Management Accounting Services (MAS)
 * - Business Strategy & Operations
 * - Advanced Auditing Concepts
 * - Corporate Governance
 * - Risk Management
 */

export interface BusinessKnowledgeEntry {
    id: string;
    domain: string; // 'management', 'auditing', 'governance', 'strategy', 'operations'
    topic: string;
    subtopic: string;
    level: 'practitioner' | 'manager' | 'executive' | 'expert';
    content: string;
    standards?: string[]; // Applicable standards (PSA, PFRS, etc.)
    practicalApplication: string;
    commonMistakes: string[];
    proTips: string[];
    relatedTopics: string[];
    lastUpdated: string;
}

// ==================== MANAGEMENT ACCOUNTING ====================

export const MANAGEMENT_ACCOUNTING_KNOWLEDGE: BusinessKnowledgeEntry[] = [
    {
        id: 'mas-001',
        domain: 'management',
        topic: 'Cost-Volume-Profit Analysis',
        subtopic: 'Advanced CVP Applications',
        level: 'manager',
        content: `## Advanced Cost-Volume-Profit Analysis

### Core Formulas

**Break-Even Point (BEP)**
\`\`\`
BEP (units) = Fixed Costs / Contribution Margin per unit
BEP (pesos) = Fixed Costs / CM Ratio

Where:
CM per unit = Selling Price - Variable Cost per unit
CM Ratio = (SP - VC) / SP = CM per unit / SP
\`\`\`

**Target Profit**
\`\`\`
Required Sales (units) = (Fixed Costs + Target Profit) / CM per unit
Required Sales (₱) = (Fixed Costs + Target Profit) / CM Ratio

For After-Tax Target:
Before-Tax Profit = After-Tax Profit / (1 - Tax Rate)
\`\`\`

**Margin of Safety (MOS)**
\`\`\`
MOS (₱) = Actual Sales - BEP Sales
MOS % = MOS / Actual Sales × 100%

Interpretation:
- Sales can drop by MOS% before losses occur
- Higher MOS = Lower risk
\`\`\`

### Multi-Product CVP Analysis

**Weighted Average Contribution Margin**
\`\`\`
Step 1: Calculate CM per unit for each product
Step 2: Multiply by sales mix ratio
Step 3: Sum all weighted CMs = Weighted Average CM

Example:
Product A: CM = ₱100, Mix = 60%  → ₱60
Product B: CM = ₱150, Mix = 40%  → ₱60
Weighted Average CM = ₱120

BEP (units) = FC / ₱120
Distribute by sales mix ratio
\`\`\`

### Operating Leverage

**Degree of Operating Leverage (DOL)**
\`\`\`
DOL = Contribution Margin / Operating Income
     = CM / (CM - FC)

Application:
% Change in Income = DOL × % Change in Sales

High DOL:
- High fixed costs relative to variable
- Greater profit sensitivity to sales changes
- Higher risk, higher reward
\`\`\`

### Sensitivity Analysis

**What-If Scenarios**
1. **Price Changes**: Analyze CM impact
2. **Cost Changes**: Separate fixed vs. variable effects
3. **Volume Changes**: Use CM ratio for quick calculation
4. **Mix Changes**: Recalculate weighted average CM

**Indifference Point**
\`\`\`
Point where two alternatives yield same profit:

TC₁ = TC₂
FC₁ + (VC₁ × Q) = FC₂ + (VC₂ × Q)

Solve for Q = Indifference point
\`\`\`

### Decision Framework

| Decision Type | CVP Application |
|---------------|-----------------|
| **Make or Buy** | Compare variable costs vs. purchase price |
| **Accept Special Order** | If price > variable cost (with excess capacity) |
| **Drop Product** | If lost CM > saved fixed costs (DON'T drop) |
| **Expand Capacity** | If added CM > added fixed costs |

### Real-World Example

**Scenario: Restaurant CVP**
- Selling Price per meal: ₱500
- Variable Cost per meal: ₱200
- Fixed Costs monthly: ₱900,000
- Target profit: ₱300,000 after 30% tax

\`\`\`computation
Step 1: Calculate CM per unit
CM = ₱500 - ₱200 = ₱300

Step 2: Before-tax target
Before-tax = ₱300,000 / (1 - 0.30) = ₱428,571

Step 3: Required sales (units)
Units = (₱900,000 + ₱428,571) / ₱300
      = 4,429 meals

Step 4: Required sales (₱)
Sales = 4,429 × ₱500 = ₱2,214,500

Step 5: Margin of Safety (if actual = 5,000 meals)
MOS = (5,000 - 3,000) × ₱500 = ₱1,000,000
MOS% = 40% (sales can drop 40% before losses)
\`\`\``,
        standards: ['Management Accounting Principles', 'CVP Analysis Framework'],
        practicalApplication: `Used for pricing decisions, profit planning, budgeting, and evaluating business alternatives. Essential for management reporting and strategic planning.`,
        commonMistakes: [
            'Forgetting to convert after-tax to before-tax profit',
            'Using wrong formula (mixing up units vs. pesos)',
            'Not separating fixed from variable costs properly',
            'Ignoring capacity constraints in calculations',
            'Miscalculating weighted average in multi-product scenarios'
        ],
        proTips: [
            'Always verify if costs are truly fixed or variable',
            'Consider relevant range - CVP assumptions break down outside it',
            'Use sensitivity analysis for key variables',
            'Update CVP models regularly as cost structures change',
            'Combine with ABC costing for better accuracy'
        ],
        relatedTopics: ['Break-Even Analysis', 'Budgeting', 'Pricing Strategy', 'Decision Making'],
        lastUpdated: '2024-12-08'
    },
    {
        id: 'mas-002',
        domain: 'management',
        topic: 'Variance Analysis',
        subtopic: 'Standard Costing and Variance Decomposition',
        level: 'manager',
        content: `## Comprehensive Variance Analysis

### Material Variances

**Material Price Variance (MPV)**
\`\`\`
MPV = (Actual Price - Standard Price) × Actual Quantity Purchased
    = (AP - SP) × AQP

Responsibility: Purchasing Department
Favorable: AP < SP (paid less than expected)
Unfavorable: AP > SP (paid more than expected)
\`\`\`

**Material Usage/Quantity Variance (MUV)**
\`\`\`
MUV = (Actual Quantity Used - Standard Quantity) × Standard Price
    = (AQU - SQ) × SP

Where: SQ = Standard Qty per unit × Actual Production

Responsibility: Production Department
Favorable: Used less material
Unfavorable: Used more material (waste, spoilage)
\`\`\`

**Total Material Variance**
\`\`\`
TMV = (AP × AQU) - (SP × SQ)
    = MPV + MUV
\`\`\`

### Labor Variances

**Labor Rate Variance (LRV)**
\`\`\`
LRV = (Actual Rate - Standard Rate) × Actual Hours
    = (AR - SR) × AH

Responsibility: HR/Production Manager
Causes: Overtime premium, skill level differences
\`\`\`

**Labor Efficiency Variance (LEV)**
\`\`\`
LEV = (Actual Hours - Standard Hours) × Standard Rate
    = (AH - SH) × SR

Where: SH = Standard Hours per unit × Actual Production

Responsibility: Production Supervisor
Causes: Worker efficiency, machine breakdowns, poor materials
\`\`\`

### Overhead Variances (3-Way Analysis)

**1. Variable Overhead Spending Variance**
\`\`\`
= (Actual Rate - Standard Rate) × Actual Hours
= Actual VOH - (SR × AH)
\`\`\`

**2. Variable Overhead Efficiency Variance**
\`\`\`
= (Actual Hours - Standard Hours) × Standard Rate
= (AH - SH) × SR
\`\`\`

**3. Fixed Overhead Volume Variance**
\`\`\`
= Budgeted FOH - Applied FOH
= Budgeted FOH - (Standard Rate × SH)

Favorable: Overutilization (produced more than budgeted)
Unfavorable: Underutilization (idle capacity)
\`\`\`

### Variance Investigation Decision

**Statistical Control Limits**
\`\`\`
Investigate if:
1. Variance > X% of standard (e.g., >5%)
2. Variance > ₱X absolute amount
3. Trend of unfavorable variances
4. Combination of percentage AND amount
\`\`\`

**Cost-Benefit Analysis**
- Cost of investigation
- Probability variance is controllable
- Benefit from correcting variance
- Investigate if: Expected Benefit > Investigation Cost

### Comprehensive Example

**Manufacturing Company - Monthly Production**

**Standards:**
- Material: 5 kg per unit @ ₱100/kg
- Labor: 2 hours per unit @ ₱200/hour
- Variable Overhead: ₱150/hour
- Fixed Overhead: ₱500,000/month (based on 10,000 hours)

**Actuals:**
- Production: 4,500 units
- Material purchased: 24,000 kg @ ₱105/kg
- Material used: 23,500 kg
- Labor hours: 9,200 @ ₱205/hour
- Variable Overhead: ₱1,400,000
- Fixed Overhead: ₱520,000

\`\`\`computation
MATERIAL VARIANCES:
SQ = 4,500 × 5 = 22,500 kg

MPV = (₱105 - ₱100) × 24,000
    = ₱120,000 Unfavorable

MUV = (23,500 - 22,500) × ₱100
    = ₱100,000 Unfavorable

LABOR VARIANCES:
SH = 4,500 × 2 = 9,000 hours

LRV = (₱205 - ₱200) × 9,200
    = ₱46,000 Unfavorable

LEV = (9,200 - 9,000) × ₱200
    = ₱40,000 Unfavorable

VARIABLE OVERHEAD:
SR = ₱150/hour

VOH Spending = ₱1,400,000 - (₱150 × 9,200)
             = ₱20,000 Unfavorable

VOH Efficiency = (9,200 - 9,000) × ₱150
               = ₱30,000 Unfavorable

FIXED OVERHEAD:
Budgeted FOH = ₱500,000
Applied FOH = (₱500,000/10,000) × 9,000 = ₱450,000

FOH Volume Variance = ₱500,000 - ₱450,000
                    = ₱50,000 Unfavorable
                    (underutilization)
\`\`\`

### Interpretation & Action

| Variance | Cause | Action |
|----------|-------|--------|
| **Unfav. MPV** | Price increase, poor purchasing | Negotiate better prices, find alt suppliers |
| **Unfav. MUV** | Waste, theft, poor quality | Improve process, better training, quality control |
| **Unfav. LRV** | Overtime, wrong skill level | Better scheduling, match skills to jobs |
| **Unfav. LEV** | Inefficiency, delays | Training, equipment maintenance, workflow optimization |
| **Unfav. VOH** | Higher rates, inefficiency | Control spending, improve efficiency |
| **Unfav. FOH Vol** | Idle capacity | Increase production, find new business |`,
        standards: ['Standard Costing Principles', 'Management Accounting Standards'],
        practicalApplication: `Essential for performance evaluation, cost control, and identifying areas for operational improvement. Used in management reporting and budgetary control systems.`,
        commonMistakes: [
            'Using wrong quantity for price variance (should be AQP, not AQU)',
            'Forgetting to multiply by standard price in quantity variance',
            'Mixing up actual hours vs. standard hours',
            'Not calculating fixed overhead volume variance',
            'Investigating all variances regardless of materiality'
        ],
        proTips: [
            'Always state whether variance is favorable or unfavorable',
            'Look for patterns - multiple unfavorable variances suggest systemic issues',
            'Price variances are usually purchasing responsibility',
            'Quantity/efficiency variances are usually production responsibility',
            'Consider interdependencies (e.g., cheaper materials → more waste)'
        ],
        relatedTopics: ['Standard Costing', 'Budgeting', 'Performance Evaluation', 'Cost Control'],
        lastUpdated: '2024-12-08'
    }
];

// ==================== ADVANCED AUDITING ====================

export const ADVANCED_AUDITING_KNOWLEDGE: BusinessKnowledgeEntry[] = [
    {
        id: 'aud-001',
        domain: 'auditing',
        topic: 'Risk Assessment',
        subtopic: 'Audit Risk Model and Risk Response',
        level: 'practitioner',
        content: `## Audit Risk Model - Deep Dive

### The Fundamental Formula

\`\`\`
AR = IR × CR × DR

Where:
AR = Audit Risk (target level, usually 5%)
IR = Inherent Risk
CR = Control Risk  
DR = Detection Risk
\`\`\`

### Risk of Material Misstatement (RMM)

\`\`\`
RMM = IR × CR

This is what the auditor ASSESSES
Detection Risk is what the auditor CONTROLS
\`\`\`

### Inverse Relationship

| RMM Level | Detection Risk | Audit Procedures |
|-----------|---------------|------------------|
| **High RMM** | **Low DR needed** | **Extensive testing** |
| Low RMM | High DR acceptable | Limited testing |

**Formula Rearrangement:**
\`\`\`
DR = AR / (IR × CR)

Example:
If AR = 5%, IR = 80%, CR = 60%
DR = 0.05 / (0.80 × 0.60) = 10.4%

Interpretation: Can accept ~10% detection risk
\`\`\`

### Inherent Risk Factors

**Financial Statement Level:**
- Management integrity/competence
- Industry/economic conditions  
- Nature of entity's business
- Results of previous audits
- Initial vs. continuing engagement
- Related party transactions
- Non-routine transactions
- Susceptibility to fraud

**Assertion Level:**
- Complexity of calculations
- Subjectivity of estimates
- Volume of transactions
- Technological sophistication
- Accounting principle changes

**High IR Examples:**
- Fair value measurements
- Revenue recognition (multiple elements)
- Inventory obsolescence
- Goodwill impairment
- Pension obligations

### Control Risk Assessment

**Steps:**
1. **Understand controls** - Interview, observation, documentation
2. **Evaluate design** - Are controls theoretically effective?
3. **Test implementation** - Are controls actually in place?
4. **Test operating effectiveness** - Do controls work throughout period?

**Control Risk Levels:**
\`\`\`
MAXIMUM (100%):
- No reliance on controls
- Substantive procedures only
- Used when controls don't exist or won't be tested

MODERATE (60-80%):
- Some reliance on controls
- Combined approach (tests of controls + substantive)

LOW (20-50%):
- High reliance on controls
- Extensive tests of controls
- Reduced substantive procedures
\`\`\`

### Detection Risk and Substantive Procedures

**Nature:**
- More effective procedures for lower DR
- Example: Confirmation vs. vouching

**Timing:**
- Year-end vs. interim testing
- Lower DR → Year-end testing

**Extent:**
- Sample size inversely related to DR
- Lower DR → Larger sample

### Practical Application Matrix

| Account | IR Assessment | CR Assessment | RMM | DR | Audit Response |
|---------|---------------|---------------|-----|-------|----------------|
| **Cash** | Low (simple) | Low (strong controls) | Low | High | Limited testing, interim OK |
| **Revenue** | High (complex, fraud risk) | Moderate | High | Low | Extensive testing, year-end, large sample |
| **Inventory** | Moderate (estimates) | Moderate | Moderate | Moderate | Balanced approach |
| **PPE** | Low (infrequent) | High (weak controls) | Moderate | Moderate | Test details, vouching |
| **Goodwill** | Very High (significant estimates) | N/A | Very High | Very Low | Extensive procedures, specialists |

### Fraud Risk Assessment (PSA 240)

**Fraud Risk Factors:**
1. **Incentive/Pressure**
   - Financial targets not met
   - Personal financial obligations
   - Excessive pressure for performance

2. **Opportunity**
   - Weak internal controls
   - Complex transactions
   - Ineffective monitoring

3. **Attitude/Rationalization**
   - Management override of controls
   - Aggressive interpretation of rules
   - History of violations

**Required Procedures:**
- **Always** presume fraud risk in revenue recognition
- Discuss fraud susceptibility with team
- Inquire of management and others
- Test journal entries
- Review accounting estimates for bias

### Audit Approach Decision Tree

\`\`\`
START
  ↓
Are controls relevant and effective?
  ├─ YES → Reliance strategy
  │         ├─ Test controls
  │         ├─ Assess CR as Low/Moderate  
  │         └─ Reduce substantive procedures
  │
  └─ NO → Substantive strategy
            ├─ Assess CR as Maximum
            ├─ No tests of controls
            └─ Extensive substantive procedures
\`\`\`

### Documentation Requirements (PSA 230)

**Must Document:**
1. Risk assessment procedures performed
2. Identified and assessed risks (FS level and assertion level)
3. Risks requiring special audit consideration
4. Linkage of procedures to assessed risks
5. Results of risk assessment

**Consequences of Poor Documentation:**
- Quality control issues
- Peer review findings
- Litigation vulnerability
- INABILITY TO DEFEND AUDIT OPINION`,
        standards: [
            'PSA 200 - Overall Objectives',
            'PSA 315 - Identifying and Assessing Risks',
            'PSA 330 - Auditor\'s Responses to Assessed Risks',
            'PSA 240 - Fraud in Financial Statement Audit'
        ],
        practicalApplication: `Foundation of audit planning and execution. Determines nature, timing, and extent of audit procedures. Critical for audit efficiency and effectiveness.`,
        commonMistakes: [
            'Treating AR formula as mathematical instead of professional judgment',
            'Not documenting risk assessment properly',
            'Assuming low control risk without testing controls',
            'Ignoring fraud risk factors',
            'Not adjusting procedures when risks change',
            'Using same audit program for all clients'
        ],
        proTips: [
            'Start with understanding the business - reduces IR assessment errors',
            'Document risk assessment contemporaneously, not after fieldwork',
            'Always presume fraud risk in revenue - PSA 240 requirement',
            'Link each procedure explicitly to assessed risk',
            'Update risk assessment if conditions change during audit',
            'Consider using audit risk model quantitatively for training, but rely on judgment for actual audits'
        ],
        relatedTopics: ['Audit Planning', 'Internal Controls', 'Substantive Procedures', 'Fraud', 'Audit Evidence'],
        lastUpdated: '2024-12-08'
    }
];

// ==================== CORPORATE GOVERNANCE ====================

export const CORPORATE_GOVERNANCE_KNOWLEDGE: BusinessKnowledgeEntry[] = [
    {
        id: 'gov-001',
        domain: 'governance',
        topic: 'Corporate Governance',
        subtopic: 'Board Responsibilities and Best Practices',
        level: 'executive',
        content: `## Corporate Governance Framework - Philippines

### Legal Framework

**Primary Laws:**
1. Revised Corporation Code (RA 11232)
2. Securities Regulation Code (RA 8799)
3. SEC Code of Corporate Governance (2016)
4. SEC Memorandum Circular No. 19 (2016)

### Board of Directors Duties

**Fiduciary Duties:**

1. **Duty of Care**
   - Act with diligence of a prudent person
   - Stay informed about corporate affairs
   - Attend board meetings regularly
   - Review financial statements
   - Make informed decisions

2. **Duty of Loyalty**
   - Act in best interest of corporation
   - Avoid conflicts of interest
   - Disclose personal interests
   - Do not usurp corporate opportunities
   - No self-dealing without full disclosure

3. **Duty of Obedience**
   - Comply with laws and regulations
   - Ensure corporation operates legally
   - Follow articles and bylaws

### Board Composition Requirements

**Listed Companies (SEC MC 19):**
\`\`\`
Minimum Board Size: 5 directors
Maximum: 15 directors

Independent Directors:
- At least 2 or 20% (whichever is higher)
- Cannot be officer/employee  
- No material business relationship
- Serve maximum 9 years
\`\`\`

**Board Committees (Mandatory for Listed):**

1. **Audit Committee**
   - At least 3 directors (majority independent)
   - Chairman must be independent
   - At least one financial expert
   - Reviews financial statements
   - Evaluates external/internal auditors

2. **Corporate Governance Committee**
   - Oversees governance framework
   - Determines compensation
   - Reviews nominations
   - Ensures compliance

3. **Related Party Transactions Committee**
   - Reviews RPTs
   - Ensures arm's length pricing
   - Protects minority shareholders

4. **Risk Management Committee** (Assets > ₱50B)
   - Identifies key risks
   - Implements risk framework
   - Reports to board

### Best Practices Matrix

| Aspect | Minimum Requirement | Best Practice |
|--------|---------------------|---------------|
| **Board Meetings** | Quarterly | Monthly |
| **Independent Directors** | 2 or 20% | 1/3 to 1/2 of board |
| **Board Diversity** | Not required | Skills, age, gender diversity |
| **Succession Planning** | Not required | Documented plan |
| **Board Evaluation** | Annual (self-assessment) | External every 3 years |
| **Director Training** | Accreditation seminar | Ongoing education |

### Performance Metrics

**Board Effectiveness Indicators:**
1. Meeting attendance rate (target: >90%)
2. Quality of deliberations
3. Strategic oversight effectiveness
4. Risk management adequacy
5. Financial performance vs. targets
6. Compliance record
7. Stakeholder satisfaction

**Red Flags:**
- Rubber-stamp board (no dissent)
- CEO dominance
- Related party abuses
- Poor attendance
- Lack of financial expertise
- No independent directors
- Inadequate disclosure`,
        standards: [
            'SEC Code of Corporate Governance (2016)',
            'SEC MC No. 19 (2016)',
            'RCC Sec. 22-25 (Board Duties)',
            'PSE Listing Rules'
        ],
        practicalApplication: `Essential for board members, corporate secretaries, compliance officers, and legal counsel. Ensures legal compliance and protects directors from liability.`,
        commonMistakes: [
            'Allowing CEO to control board agenda',
            'Not documenting dissenting votes',
            'Inadequate independent director representation',
            'Failing to evaluate RPTs properly',
            'No board succession planning',
            'Insufficient director training'
        ],
        proTips: [
            'Document everything - minutes are your protection',
            'Independent directors must truly be independent',
            'Board should set tone at the top for ethics',
            'Regular executive sessions without management present',
            'Invest in director education and development',
            'Use board portals for secure document sharing'
        ],
        relatedTopics: ['Securities Regulation', 'Corporate Law', 'Risk Management', 'Audit'],
        lastUpdated: '2024-12-08'
    }
];

// ==================== EXPORT ALL ====================

export const ENTERPRISE_BUSINESS_KNOWLEDGE = {
    management: MANAGEMENT_ACCOUNTING_KNOWLEDGE,
    auditing: ADVANCED_AUDITING_KNOWLEDGE,
    governance: CORPORATE_GOVERNANCE_KNOWLEDGE
};

export function searchEnterpriseBusinessKnowledge(query: string): BusinessKnowledgeEntry[] {
    const allKnowledge = [
        ...MANAGEMENT_ACCOUNTING_KNOWLEDGE,
        ...ADVANCED_AUDITING_KNOWLEDGE,
        ...CORPORATE_GOVERNANCE_KNOWLEDGE
    ];
    
    const lowerQuery = query.toLowerCase();
    
    return allKnowledge.filter(entry =>
        entry.topic.toLowerCase().includes(lowerQuery) ||
        entry.subtopic.toLowerCase().includes(lowerQuery) ||
        entry.content.toLowerCase().includes(lowerQuery) ||
        (entry.standards && entry.standards.some(std => std.toLowerCase().includes(lowerQuery)))
    ).sort((a, b) => {
        // Prioritize by relevance
        const aRelevance = a.topic.toLowerCase().includes(lowerQuery) ? 1 : 0;
        const bRelevance = b.topic.toLowerCase().includes(lowerQuery) ? 1 : 0;
        return bRelevance - aRelevance;
    });
}

export default ENTERPRISE_BUSINESS_KNOWLEDGE;
